import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { useChannel } from '@/context/ChannelContext'
import { useShowcase } from '@/showcase/useShowcase'
import {
  getShowcaseOverview,
  getShowcasePosts,
  PostsCountsByStatus,
  ShowcaseOverview,
  ShowcaseState,
  VisitsBucket,
} from '@/api/showcase'
import { getSubscription, Subscription } from '@/api/subscription'
import { QualityRejectedBlock } from './QualityRejectedBlock'

/**
 * Обзор Витрины — главный экран таба «Обзор».
 *
 * Диспетчер по бэк-статусу + дням с активации:
 *  - active + дней < 3   → State_0_3   «Создаём страницы»
 *  - active + 3 ≤ дней < 14 → State_3_14 «Страницы готовы — ждём поисковики»
 *  - active + дней ≥ 14  → State_14plus полный дашборд (placeholder, ждёт B7)
 *  - disabled            → State_Disabled
 *  - grace_3d            → State_Grace (баннер «восстановите Профи в N дней»)
 *
 * Early-promote на State_14plus: если у канала уже есть факт visits (visits.last30d.visits > 0),
 * показываем полный дашборд даже до 14-го дня. Иначе юзер видит «ждём поисковики», хотя по факту
 * читатели уже идут.
 *
 * Состояния processing/consent/choosing/intro живут в ActivationFlow,
 * сюда не попадают — ShowcaseRoute их перехватывает.
 */
export function OverviewPage() {
  const { selectedChannel } = useChannel()
  const { state } = useShowcase(selectedChannel?.uuid)
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  // Загружаем overview в верхнем компоненте, чтобы решить early-promote
  // на State_14plus при наличии реальных visits. State_14plus умеет принимать
  // уже загруженный overview через props — без дублирующего запроса.
  const [overview, setOverview] = useState<ShowcaseOverview | null>(null)

  useEffect(() => {
    if (!selectedChannel?.uuid || state?.status !== 'active') return
    getShowcaseOverview(selectedChannel.uuid)
      .then(setOverview)
      .catch(() => setOverview(null))
  }, [selectedChannel?.uuid, state?.status])

  // Subscription тянем параллельно — нужно для info-плашки cancelled+active
  // (контракт B 16.06: showcase.status и subscription.state — независимые домены).
  useEffect(() => {
    if (!selectedChannel?.uuid) return
    getSubscription(selectedChannel.uuid)
      .then(setSubscription)
      .catch(() => setSubscription(null))
  }, [selectedChannel?.uuid])

  if (!state) return null

  // Бэйдж «N постов не прошли проверку» — показываем над основным контентом
  // в любом active/grace/disabled-состоянии. Если квалити-чистый — компонент
  // сам ничего не рендерит.
  const qrBadge = selectedChannel ? (
    <QualityRejectedBlock channelUUID={selectedChannel.uuid} />
  ) : null

  // Info-плашка «Витрина работает до DD.MM.YYYY» — только при
  // showcase.status='active' AND subscription.state='cancelled'. Юзер отменил
  // автопродление, доступ остался до accessEndsAt.
  const showCancelledNote =
    state.status === 'active' &&
    subscription?.state === 'cancelled' &&
    !!subscription.accessEndsAt
  const cancelledNote = showCancelledNote ? (
    <CancelledAccessNote endsAt={subscription.accessEndsAt!} />
  ) : null

  if (state.status === 'disabled') {
    return <>{qrBadge}<StateDisabled /></>
  }
  if (state.status === 'grace_3d') {
    return <>{qrBadge}<StateGrace state={state} /></>
  }

  // active — раскладываем по дням с активации.
  // Early-promote: если уже есть факт visits (хоть один читатель за 30 дней) —
  // показываем полный дашборд раньше 14-го дня. Это снимает confusion «у меня
  // уже читатели, а UI пишет «индексация»».
  const days = daysSince(state.activatedAt)
  const hasRealVisits = (overview?.visits.last30d.visits ?? 0) > 0
  if (hasRealVisits) {
    return <>{qrBadge}{cancelledNote}<State_14plus channelUUID={selectedChannel?.uuid} preloaded={overview} /></>
  }
  if (days < 3) return <>{qrBadge}{cancelledNote}<State_0_3 days={days} channelUUID={selectedChannel?.uuid} /></>
  if (days < 14) return <>{qrBadge}{cancelledNote}<State_3_14 days={days} /></>
  return <>{qrBadge}{cancelledNote}<State_14plus channelUUID={selectedChannel?.uuid} preloaded={overview} /></>
}

function CancelledAccessNote({ endsAt }: { endsAt: string }) {
  const navigate = useNavigate()
  const formatted = formatRuDate(endsAt)
  return (
    <div className="cn-note">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
      <div className="cn-body">
        <div>
          Витрина работает до&nbsp;<b>{formatted}</b>. После этой даты потребуется новое пополнение или возобновление автопродления в&nbsp;Биллинге.
        </div>
        <button className="cn-link" onClick={() => navigate('/billing')}>
          Перейти в&nbsp;Биллинг →
        </button>
      </div>
      <style>{`
        .cn-note {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 16px;
          margin-bottom: 16px;
          background: var(--tg-paper, #fafafd);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          font-size: 13px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
        }
        .cn-note svg { width: 18px; height: 18px; color: var(--tg-mute, #5b6678); flex-shrink: 0; margin-top: 1px; }
        .cn-body { display: flex; flex-direction: column; gap: 4px; flex: 1; }
        .cn-body b { color: var(--tg-ink, #111); font-weight: 700; }
        .cn-link {
          align-self: flex-start;
          background: none;
          border: 0;
          padding: 0;
          font-family: inherit;
          font-size: 12.5px;
          color: var(--tg-blue-press, #0052cc);
          font-weight: 600;
          cursor: pointer;
        }
        .cn-link:hover { text-decoration: underline; }
      `}</style>
    </div>
  )
}

function formatRuDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function daysSince(iso?: string): number {
  if (!iso) return 0
  const ms = Date.now() - new Date(iso).getTime()
  return Math.max(0, Math.floor(ms / 86_400_000))
}

function computeDaysLeft(state: ShowcaseState): number {
  // Если бэк прислал готовое значение — используем
  if (typeof state.graceDaysLeft === 'number') return Math.max(0, state.graceDaysLeft)
  // Иначе считаем от pendingDeindexAt
  if (!state.pendingDeindexAt) return 0
  const ms = new Date(state.pendingDeindexAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(ms / 86_400_000))
}

// ───────── День 0-3 ─────────

function State_0_3({ days, channelUUID }: { days: number; channelUUID?: string }) {
  const [counts, setCounts] = useState<PostsCountsByStatus | null>(null)

  useEffect(() => {
    if (!channelUUID) return
    getShowcasePosts(channelUUID, { limit: 1 })
      .then((res) => {
        const raw = (res.countsByStatus
          ?? (res as unknown as { counts_by_status?: Record<string, number> }).counts_by_status
          ?? null) as Record<string, number> | null
        if (raw) {
          setCounts({
            queued: raw.queued ?? 0,
            processing: raw.processing ?? 0,
            published: raw.published ?? 0,
            quality_rejected: raw.qualityRejected ?? raw.quality_rejected ?? 0,
            deindexed: raw.deindexed ?? 0,
            failed: raw.failed ?? 0,
          })
        }
      })
      .catch(() => setCounts(null))
  }, [channelUUID])

  const inProgress = counts ? counts.queued + counts.processing : null
  const published = counts?.published ?? null
  const total = counts
    ? counts.queued + counts.processing + counts.published + counts.quality_rejected + counts.deindexed
    : null

  return (
    <div className="ov-state">
      <div className="ov-card ov-card-pulse">
        <div className="ov-anim">
          <div className="ov-anim-circle" />
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 5h16M4 12h16M4 19h7" />
          </svg>
        </div>

        <h2 className="ov-h2">Создаём страницы постов</h2>
        <p className="ov-sub">
          Это занимает несколько часов. Делать ничего не нужно — просто заходите завтра, и&nbsp;вы&nbsp;увидите
          сколько страниц уже готово к&nbsp;поиску.
        </p>

        {counts && total !== null && total > 0 ? (
          <div className="ov-progress-real">
            <div className="ov-prog-real-row">
              <div className="ov-prog-real-num">
                <b>{(published ?? 0).toLocaleString('ru-RU')}</b>
                <span>опубликовано</span>
              </div>
              <div className="ov-prog-real-sep">/</div>
              <div className="ov-prog-real-num">
                <b>{total.toLocaleString('ru-RU')}</b>
                <span>всего в&nbsp;работе</span>
              </div>
              {inProgress !== null && inProgress > 0 && (
                <>
                  <div className="ov-prog-real-sep">·</div>
                  <div className="ov-prog-real-num ov-prog-real-num--small">
                    <b>{inProgress.toLocaleString('ru-RU')}</b>
                    <span>в&nbsp;очереди</span>
                  </div>
                </>
              )}
            </div>
            <div className="ov-prog-bar">
              <div
                className="ov-prog-fill"
                style={{ width: `${total > 0 ? Math.min(100, ((published ?? 0) / total) * 100) : 0}%` }}
              />
            </div>
            <div className="ov-prog-meta">
              <span>День {days + 1} из&nbsp;3</span>
              <span className="ov-prog-eta">≈ 1-3 часа</span>
            </div>
          </div>
        ) : (
          <div className="ov-progress">
            <div className="ov-prog-bar">
              <div className="ov-prog-fill ov-prog-anim" />
            </div>
            <div className="ov-prog-meta">
              <span>День {days + 1} из&nbsp;3 — обработка</span>
              <span className="ov-prog-eta">≈ 1-3 часа</span>
            </div>
          </div>
        )}
      </div>

      <div className="ov-tip">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
        <span>
          <b>Графиков пока нет</b> — они появятся когда поисковики начнут присылать первых читателей. Это
          обычно 5-14 дней после публикации.
        </span>
      </div>

      <Styles />
    </div>
  )
}

// ───────── День 3-14 ─────────

function State_3_14({ days }: { days: number }) {
  return (
    <div className="ov-state">
      <div className="ov-card">
        <h2 className="ov-h2">Страницы готовы. Ждём поисковики</h2>
        <p className="ov-sub">
          Дальше — нужно время чтобы Яндекс и&nbsp;Google нашли ваши страницы, добавили в&nbsp;поиск
          и&nbsp;начали показывать читателям. Обычно <b>1-2 недели</b> от&nbsp;дня публикации.
        </p>

        <div className="ov-mini-steps">
          <MiniStep status="done" label="Страницы опубликованы" sub="Каждый пост — отдельная страница на портале" />
          <MiniStep status="active" label="Поисковики проверяют" sub={`День ${days} — Яндекс и Google индексируют контент`} />
          <MiniStep status="pending" label="Первые читатели из поиска" sub="Когда страницы попадут в выдачу — пойдёт трафик" />
        </div>

        <div className="ov-relax">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>
            <b>Ничего настраивать не нужно.</b> Просто заходите через несколько дней — здесь появятся
            метрики и&nbsp;графики.
          </span>
        </div>
      </div>

      <Styles />
    </div>
  )
}

// ───────── День 14+ (placeholder для B7) ─────────

function State_14plus({ channelUUID, preloaded }: { channelUUID?: string; preloaded?: ShowcaseOverview | null }) {
  const [overview, setOverview] = useState<ShowcaseOverview | null>(preloaded ?? null)
  const [loading, setLoading] = useState(!preloaded)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // Если родитель уже прокинул overview — используем его, не дёргаем повторно
    if (preloaded) {
      setOverview(preloaded)
      setLoading(false)
      return
    }
    if (!channelUUID) return
    setLoading(true)
    setError(null)
    getShowcaseOverview(channelUUID)
      .then((res) => setOverview(res))
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false))
  }, [channelUUID, preloaded])

  if (loading) {
    return (
      <div className="ov-state">
        <div className="ov-card">
          <div className="ov-skel-grid">
            <div className="ov-skel-kpi" />
            <div className="ov-skel-kpi" />
          </div>
        </div>
        <Styles />
      </div>
    )
  }

  if (error || !overview) {
    return (
      <div className="ov-state">
        <div className="ov-card">
          <h2 className="ov-h2">Не удалось загрузить данные</h2>
          <p className="ov-sub">{error || 'Попробуйте обновить страницу'}</p>
        </div>
        <Styles />
      </div>
    )
  }

  const last7 = overview.visits.last7d
  const last30 = overview.visits.last30d
  // Темп: сравниваем 7д vs пропорциональные 7 дней из 30д. (30-7)/3 ≈ avg-week.
  const prevWeekEstimate = Math.max(0, Math.round((last30.visits - last7.visits) / (23 / 7)))
  const trend = prevWeekEstimate > 0
    ? Math.round(((last7.visits - prevWeekEstimate) / prevWeekEstimate) * 100)
    : null

  return (
    <div className="ov-state">
      <div className="ov-card">
        <h2 className="ov-h2">Читателей из&nbsp;поиска</h2>

        <div className="ov-kpis">
          <KpiBlock label="За 7 дней" bucket={last7} trendPct={trend} />
          <KpiBlock label="За 30 дней" bucket={last30} trendPct={null} />
        </div>

        <SourcesMini totals={last30} />

        <SearchMetricsHero metrics={overview.searchMetrics} />
      </div>

      <Styles />
    </div>
  )
}

function KpiBlock({ label, bucket, trendPct }: { label: string; bucket: VisitsBucket; trendPct: number | null }) {
  return (
    <div className="ov-kpi">
      <div className="ov-kpi-label">{label}</div>
      <div className="ov-kpi-value">{bucket.visits.toLocaleString('ru-RU')}</div>
      {trendPct !== null && (
        <div className={`ov-kpi-trend ${trendPct >= 0 ? 'up' : 'down'}`}>
          {trendPct >= 0 ? '↑' : '↓'} {Math.abs(trendPct)}% к&nbsp;предыдущей неделе
        </div>
      )}
    </div>
  )
}

function SourcesMini({ totals }: { totals: VisitsBucket }) {
  if (totals.visits === 0) {
    return (
      <div className="ov-src-empty">
        За 30 дней ещё никто не приходил из&nbsp;поиска. Если Витрина работает больше 2&nbsp;недель, проверьте,
        прошли ли посты quality-фильтр в&nbsp;настройках.
      </div>
    )
  }
  const items = [
    { key: 'yandex', label: 'Яндекс', value: totals.yandex, color: '#ff5a5a' },
    { key: 'google', label: 'Google', value: totals.google, color: '#1a73e8' },
    { key: 'other', label: 'Прочее', value: totals.other, color: '#9ca3af' },
  ]
  return (
    <div className="ov-src">
      <div className="ov-src-head">Откуда приходят (30д)</div>
      <div className="ov-src-bar">
        {items.map((it) => (
          <div
            key={it.key}
            className="ov-src-seg"
            style={{ flexGrow: it.value, background: it.color }}
            title={`${it.label}: ${it.value} (${Math.round((it.value / totals.visits) * 100)}%)`}
          />
        ))}
      </div>
      <div className="ov-src-legend">
        {items.map((it) => (
          <div key={it.key} className="ov-src-li">
            <span className="ov-src-dot" style={{ background: it.color }} />
            <span>{it.label}</span>
            <b>{it.value.toLocaleString('ru-RU')}</b>
            <span className="ov-src-pct">{Math.round((it.value / totals.visits) * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function SearchMetricsHero({ metrics }: { metrics: ShowcaseOverview['searchMetrics'] }) {
  const [range, setRange] = useState<'last7d' | 'last30d'>('last30d')

  if (metrics.status === 'pending_oauth') {
    return (
      <div className="ov-sm-pending">
        🔍 <strong>Подключаем Я.Вебмастер и&nbsp;Google&nbsp;Search&nbsp;Console.</strong> Скоро здесь появится: показы,
        средняя позиция, топ-запросы.
      </div>
    )
  }

  const win = range === 'last7d' ? metrics.last7d : metrics.last30d
  // scope='site' — это агрегат по всему telegraphyx.ru (см. контракт B15).
  // Per-channel данные появятся когда GSC поедет (B16). Виктор просил
  // явно подписать что цифры пока общие.
  const isSiteWide = metrics.scope === 'site'

  return (
    <div className="ov-sm">
      <div className="ov-sm-toprow">
        <div>
          <div className="ov-sm-head">Поиск {isSiteWide ? '· общий по платформе' : ''}</div>
          {isSiteWide && (
            <div className="ov-sm-scope" title="Site-level scope от Я.Вебмастера">
              Это совокупные SEO-цифры всего портала <b>telegraphyx.ru</b>. Per-канал данные подключим после Google&nbsp;Search&nbsp;Console.
            </div>
          )}
        </div>
        <RangeSwitch7v30 value={range} onChange={setRange} />
      </div>

      <div className="ov-sm-kpis">
        <div><b>{win.impressions.toLocaleString('ru-RU')}</b><span>показов</span></div>
        <div><b>{win.clicks.toLocaleString('ru-RU')}</b><span>переходов</span></div>
        <div><b>{win.avgPosition.toFixed(1)}</b><span>сред. позиция</span></div>
      </div>

      {win.topQueries.length > 0 && (
        <div className="ov-sm-queries">
          <div className="ov-sm-q-head">Топ запросов · {range === 'last7d' ? '7д' : '30д'}</div>
          {win.topQueries.slice(0, 5).map((q, i) => (
            <div key={`${q.query}-${i}`} className="ov-sm-q-row">
              <span className="ov-sm-q-text">{q.query}</span>
              <span className="ov-sm-q-meta">
                <b>{q.clicks.toLocaleString('ru-RU')}</b>/{q.impressions.toLocaleString('ru-RU')}
                <span className="ov-sm-q-pos">поз.&nbsp;{q.position.toFixed(1)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="ov-sm-foot">
        Источник: {sourceLabel(metrics.source)} · обновлено {formatSnapshot(metrics.snapshotAt)}
      </div>
    </div>
  )
}

function RangeSwitch7v30({ value, onChange }: { value: 'last7d' | 'last30d'; onChange: (r: 'last7d' | 'last30d') => void }) {
  return (
    <div className="ov-sm-rng">
      <button className={value === 'last7d' ? 'on' : ''} onClick={() => onChange('last7d')}>7д</button>
      <button className={value === 'last30d' ? 'on' : ''} onClick={() => onChange('last30d')}>30д</button>
    </div>
  )
}

function sourceLabel(s: string): string {
  if (s === 'yandex_webmaster') return 'Я.Вебмастер'
  if (s === 'gsc') return 'Google Search Console'
  if (s === 'hybrid') return 'Я.Вебмастер + GSC'
  return s
}

function formatSnapshot(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const now = Date.now()
  const diffH = Math.round((now - d.getTime()) / 3_600_000)
  if (diffH < 1) return 'только что'
  if (diffH < 24) return `${diffH}ч назад`
  const diffD = Math.round(diffH / 24)
  if (diffD === 1) return 'вчера'
  if (diffD < 7) return `${diffD} дн. назад`
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })
}

// ───────── disabled ─────────

function StateDisabled() {
  const navigate = useNavigate()
  return (
    <div className="ov-state">
      <div className="ov-card">
        <div className="ov-placeholder-icon ov-icon-mute">📵</div>
        <h2 className="ov-h2">Витрина выключена</h2>
        <p className="ov-sub">
          Страницы убраны с&nbsp;портала. Поисковики постепенно удалят их&nbsp;из&nbsp;выдачи в&nbsp;течение
          5-14&nbsp;дней — больше из&nbsp;Яндекса и&nbsp;Google подписчики приходить не&nbsp;будут.
        </p>
        <p className="ov-sub">
          Включить можно в&nbsp;любой момент — попадание в&nbsp;поиск обычно занимает 2-4&nbsp;недели после
          включения.
        </p>
        <div className="ov-actions">
          <button className="ov-cta-primary" onClick={() => navigate('/showcase/settings')}>
            Включить заново
          </button>
        </div>
      </div>

      <Styles />
    </div>
  )
}

// ───────── grace_3d ─────────

function StateGrace({ state }: { state: ShowcaseState }) {
  const navigate = useNavigate()
  // Бэк может прислать готовое graceDaysLeft, но рекомендация B (11.06):
  // считаем дни сами из pendingDeindexAt (это надёжнее — UI всегда актуален
  // даже если state кеширован).
  const daysLeft = computeDaysLeft(state)
  return (
    <div className="ov-state">
      <div className="ov-card ov-card-warn">
        <div className="ov-grace-header">
          <div className="ov-grace-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
          </div>
          <div>
            <div className="ov-grace-eyebrow">Срочно</div>
            <h2 className="ov-h2">
              Восстановите Профи в&nbsp;течение <em>{daysLeft}&nbsp;{pluralDays(daysLeft)}</em>
            </h2>
          </div>
        </div>
        <p className="ov-sub">
          Тариф Профи истёк, но&nbsp;ваши страницы пока остаются в&nbsp;поиске. Если оплатите Профи за&nbsp;{daysLeft}
          &nbsp;{pluralDays(daysLeft)} — выдача сохранится, ничего не&nbsp;изменится. Не&nbsp;успеете — мы&nbsp;уберём
          страницы, и&nbsp;Яндекс с&nbsp;Google удалят их&nbsp;из&nbsp;выдачи за&nbsp;5-14&nbsp;дней.
        </p>
        <div className="ov-actions">
          <button className="ov-cta-primary" onClick={() => navigate('/billing')}>
            Восстановить Профи — 4&nbsp;900&nbsp;₽/мес
          </button>
        </div>
      </div>

      <Styles />
    </div>
  )
}

function pluralDays(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'день'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня'
  return 'дней'
}

// ───────── shared bits ─────────

function MiniStep({ status, label, sub }: { status: 'done' | 'active' | 'pending'; label: string; sub: string }) {
  return (
    <div className={`ov-step ov-step-${status}`}>
      <div className="ov-step-dot">
        {status === 'done' ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 6L9 17l-5-5" />
          </svg>
        ) : status === 'active' ? (
          <div className="ov-step-active-pulse" />
        ) : null}
      </div>
      <div>
        <div className="ov-step-label">{label}</div>
        <div className="ov-step-sub">{sub}</div>
      </div>
    </div>
  )
}

function Styles() {
  return (
    <style>{`
      .ov-state { display: flex; flex-direction: column; gap: 18px; }
      .ov-card {
        background: var(--tg-surface, #fff);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 16px;
        padding: 28px 32px;
        display: flex;
        flex-direction: column;
        gap: 14px;
      }
      .ov-card-warn {
        background: linear-gradient(135deg, #fff8e1, #fff4cc);
        border-color: #f6c247;
      }
      .ov-card-pulse {
        background:
          radial-gradient(ellipse 60% 50% at 50% 0%, rgba(0,102,255,0.04), transparent 60%),
          var(--tg-surface, #fff);
      }
      .ov-h2 {
        font-size: 22px;
        font-weight: 800;
        color: var(--tg-ink, #111);
        margin: 0;
        letter-spacing: -0.01em;
      }
      .ov-h2 em {
        font-style: normal;
        color: var(--tg-blue-press, #0052cc);
      }
      .ov-sub {
        font-size: 14.5px;
        color: var(--tg-ink-2, #333);
        line-height: 1.55;
        margin: 0;
        max-width: 640px;
      }
      .ov-anim {
        position: relative;
        width: 64px;
        height: 64px;
        color: var(--tg-blue, #0066ff);
        margin-bottom: 4px;
      }
      .ov-anim svg { width: 100%; height: 100%; position: relative; z-index: 2; }
      .ov-anim-circle {
        position: absolute;
        inset: 0;
        border-radius: 16px;
        background: rgba(0,102,255,0.15);
        animation: ov-pulse 1.8s ease-out infinite;
      }
      @keyframes ov-pulse {
        0% { transform: scale(0.7); opacity: 0.7; }
        100% { transform: scale(1.3); opacity: 0; }
      }
      .ov-progress { display: flex; flex-direction: column; gap: 6px; max-width: 560px; }
      .ov-prog-bar {
        height: 8px;
        border-radius: 999px;
        background: var(--tg-line-2, #f0f0f4);
        overflow: hidden;
      }
      .ov-prog-fill {
        height: 100%;
        background: linear-gradient(90deg, var(--tg-blue, #0066ff), #5a8dff);
        border-radius: 999px;
      }
      .ov-prog-anim {
        width: 40%;
        animation: ov-prog-march 1.6s ease-in-out infinite;
      }
      @keyframes ov-prog-march {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(250%); }
      }
      .ov-prog-meta {
        display: flex;
        justify-content: space-between;
        font-size: 12.5px;
        color: var(--tg-mute, #5b6678);
      }
      .ov-prog-eta { font-variant-numeric: tabular-nums; }
      .ov-tip {
        background: var(--tg-paper, #fafafd);
        border: 1px solid var(--tg-line-2, #f0f0f4);
        border-radius: 12px;
        padding: 14px 18px;
        display: flex;
        align-items: flex-start;
        gap: 10px;
        font-size: 13px;
        color: var(--tg-ink-2, #333);
        line-height: 1.5;
      }
      .ov-tip svg { width: 18px; height: 18px; color: var(--tg-blue, #0066ff); flex-shrink: 0; margin-top: 1px; }
      .ov-todo-note {
        margin-top: 8px;
        font-size: 12px;
        color: var(--tg-blue-press, #0052cc);
        background: var(--tg-blue-bg, #ebf2ff);
        padding: 8px 14px;
        border-radius: 8px;
      }
      .ov-progress-real {
        display: flex;
        flex-direction: column;
        gap: 10px;
        max-width: 560px;
      }
      .ov-prog-real-row {
        display: flex;
        align-items: baseline;
        gap: 12px;
        flex-wrap: wrap;
      }
      .ov-prog-real-num {
        display: flex;
        flex-direction: column;
      }
      .ov-prog-real-num b {
        font-size: 28px;
        font-weight: 800;
        color: var(--tg-ink, #111);
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }
      .ov-prog-real-num span {
        font-size: 11px;
        color: var(--tg-mute, #5b6678);
        margin-top: 4px;
      }
      .ov-prog-real-num--small b {
        font-size: 18px;
        color: var(--tg-ink-2, #333);
      }
      .ov-prog-real-sep {
        font-size: 24px;
        color: var(--tg-line-3, #d0d0d8);
        font-weight: 300;
      }
      .ov-mini-steps {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 6px;
      }
      .ov-step {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 12px 14px;
        background: var(--tg-paper, #fafafd);
        border-radius: 12px;
      }
      .ov-step-dot {
        width: 26px;
        height: 26px;
        border-radius: 50%;
        background: var(--tg-line-2, #f0f0f4);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        flex-shrink: 0;
      }
      .ov-step-dot svg { width: 13px; height: 13px; }
      .ov-step-done .ov-step-dot { background: var(--tg-up, #00a860); }
      .ov-step-active .ov-step-dot { background: var(--tg-blue, #0066ff); position: relative; }
      .ov-step-active-pulse {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: white;
        animation: ov-step-blink 1s ease-in-out infinite;
      }
      @keyframes ov-step-blink { 50% { opacity: 0.4; } }
      .ov-step-pending { opacity: 0.55; }
      .ov-step-label { font-size: 13.5px; font-weight: 700; color: var(--tg-ink, #111); margin-bottom: 2px; }
      .ov-step-sub { font-size: 12px; color: var(--tg-mute, #5b6678); }
      .ov-relax {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        padding: 12px 16px;
        background: rgba(0, 168, 96, 0.08);
        border: 1px solid rgba(0, 168, 96, 0.2);
        border-radius: 10px;
        font-size: 13px;
        line-height: 1.5;
      }
      .ov-relax svg { width: 18px; height: 18px; color: var(--tg-up, #00a860); flex-shrink: 0; margin-top: 1px; }
      .ov-placeholder-icon {
        font-size: 40px;
        margin-bottom: 4px;
      }
      .ov-icon-mute { opacity: 0.55; }
      .ov-actions { display: flex; gap: 10px; margin-top: 6px; }
      .ov-cta-primary {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 11px 20px;
        background: var(--tg-blue, #0066ff);
        color: white;
        font-weight: 700;
        font-size: 13.5px;
        border: 0;
        border-radius: 10px;
        cursor: pointer;
        font-family: inherit;
      }
      .ov-cta-primary:hover { background: var(--tg-blue-press, #0052cc); }
      .ov-grace-header { display: flex; gap: 14px; align-items: flex-start; }
      .ov-grace-icon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        background: white;
        color: #8b6914;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }
      .ov-grace-icon svg { width: 22px; height: 22px; }
      .ov-grace-eyebrow {
        font-size: 10.5px;
        font-weight: 800;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: #8b6914;
        margin-bottom: 4px;
      }
      /* B7 dashboard */
      .ov-kpis {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px;
      }
      @media (max-width: 600px) {
        .ov-kpis { grid-template-columns: 1fr; }
      }
      .ov-kpi {
        padding: 16px 18px;
        background: var(--tg-paper, #fafafd);
        border-radius: 12px;
      }
      .ov-kpi-label {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--tg-mute, #5b6678);
        margin-bottom: 6px;
      }
      .ov-kpi-value {
        font-size: 36px;
        font-weight: 800;
        color: var(--tg-ink, #111);
        line-height: 1;
        font-variant-numeric: tabular-nums;
      }
      .ov-kpi-trend {
        margin-top: 6px;
        font-size: 12px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
      }
      .ov-kpi-trend.up { color: var(--tg-up, #00a860); }
      .ov-kpi-trend.down { color: #c53030; }
      .ov-src { display: flex; flex-direction: column; gap: 8px; }
      .ov-src-head {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--tg-mute, #5b6678);
      }
      .ov-src-bar {
        display: flex;
        height: 10px;
        border-radius: 5px;
        overflow: hidden;
        background: var(--tg-paper, #fafafd);
        gap: 2px;
      }
      .ov-src-seg { min-width: 0; }
      .ov-src-legend { display: flex; gap: 18px; flex-wrap: wrap; }
      .ov-src-li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
      }
      .ov-src-li b { color: var(--tg-ink, #111); }
      .ov-src-dot { width: 8px; height: 8px; border-radius: 50%; }
      .ov-src-pct { color: var(--tg-mute, #5b6678); }
      .ov-src-empty {
        padding: 16px;
        background: var(--tg-paper, #fafafd);
        color: var(--tg-mute, #5b6678);
        border-radius: 10px;
        font-size: 13px;
        line-height: 1.5;
      }
      .ov-sm-pending {
        padding: 12px 14px;
        background: var(--tg-blue-bg, #ebf2ff);
        color: var(--tg-blue-press, #0052cc);
        border-radius: 10px;
        font-size: 13px;
        line-height: 1.5;
      }
      .ov-sm-pending strong { font-weight: 700; }
      .ov-sm-head {
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: var(--tg-mute, #5b6678);
        margin-bottom: 6px;
      }
      .ov-sm-toprow {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 10px;
      }
      .ov-sm-scope {
        font-size: 12px;
        color: var(--tg-mute, #5b6678);
        line-height: 1.5;
        max-width: 560px;
      }
      .ov-sm-scope b { color: var(--tg-ink, #111); font-weight: 700; }
      .ov-sm-rng {
        display: inline-flex;
        background: var(--tg-paper, #fafafd);
        border-radius: 8px;
        padding: 2px;
        flex-shrink: 0;
      }
      .ov-sm-rng button {
        padding: 5px 12px;
        font-family: inherit;
        font-size: 12px;
        font-weight: 700;
        background: transparent;
        border: 0;
        color: var(--tg-mute, #5b6678);
        border-radius: 6px;
        cursor: pointer;
      }
      .ov-sm-rng button.on {
        background: var(--tg-surface, #fff);
        color: var(--tg-ink, #111);
        box-shadow: 0 1px 2px rgba(0,0,0,0.06);
      }
      .ov-sm-queries {
        display: flex;
        flex-direction: column;
        gap: 4px;
        margin-top: 10px;
      }
      .ov-sm-q-head {
        font-size: 11px;
        color: var(--tg-mute, #5b6678);
        margin-bottom: 4px;
        font-weight: 600;
      }
      .ov-sm-q-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 6px 0;
        border-bottom: 1px solid var(--tg-line-2, #f0f0f4);
        font-size: 12.5px;
      }
      .ov-sm-q-row:last-child { border-bottom: 0; }
      .ov-sm-q-text {
        color: var(--tg-ink, #111);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
        flex: 1;
      }
      .ov-sm-q-meta {
        display: inline-flex;
        align-items: baseline;
        gap: 10px;
        font-variant-numeric: tabular-nums;
        color: var(--tg-mute, #5b6678);
        flex-shrink: 0;
      }
      .ov-sm-q-meta b { color: var(--tg-ink, #111); font-weight: 700; }
      .ov-sm-q-pos { font-size: 11.5px; }
      .ov-sm-foot {
        font-size: 11px;
        color: var(--tg-mute, #5b6678);
        margin-top: 10px;
        font-variant-numeric: tabular-nums;
      }
      .ov-sm-kpis {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
      }
      .ov-sm-kpis > div {
        padding: 10px 12px;
        background: var(--tg-paper, #fafafd);
        border-radius: 8px;
        display: flex;
        flex-direction: column;
      }
      .ov-sm-kpis b {
        font-size: 18px;
        font-weight: 800;
        color: var(--tg-ink, #111);
      }
      .ov-sm-kpis span {
        font-size: 11px;
        color: var(--tg-mute, #5b6678);
        margin-top: 1px;
      }
      .ov-skel-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .ov-skel-kpi {
        height: 92px;
        background: linear-gradient(90deg, #eef0f3 0%, #f6f7f9 50%, #eef0f3 100%);
        background-size: 200% 100%;
        border-radius: 12px;
        animation: sk 1.2s linear infinite;
      }
      @keyframes sk { to { background-position: -200% 0; } }
      @media (max-width: 600px) {
        .ov-card { padding: 22px 20px; }
        .ov-h2 { font-size: 19px; }
      }
    `}</style>
  )
}
